// utilies/laneMarkerTools/Style.js
import * as Cesium from "cesium";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import Type from "../Types";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

let laneMarkerStyleModeEnabled = false;
let handler = null;
let keyDownHandler = null;

let laneMarkerDs = null;

// Selection
const selectedIds = new Set(); // entity.id
const originalStyles = new Map(); // id -> snapshot

// Drag box
let dragging = false;
let dragStart = null;
let dragEnd = null;
let dragMoved = false;

// Overlay
let overlay = null;

// Camera lock
let cameraLocked = false;
let originalCameraControlsRef = null;
// Esc callback
let onRequestDisableRef = null;

// Cancel token for async pick
let pickOpToken = 0;

function now() {
  return Cesium.JulianDate.now();
}

function ensureProperties(entity) {
  if (!entity.properties) entity.properties = new Cesium.PropertyBag();
  return entity.properties;
}

function setProp(entity, key, value) {
  const p = ensureProperties(entity);

  if (typeof p.addProperty === "function" && !p[key]) {
    p.addProperty(key);
  }

  p[key] = new Cesium.ConstantProperty(value);
}

function getProp(entity, key) {
  if (!entity || !entity.properties) return undefined;
  const prop = entity.properties[key];
  if (!prop) return undefined;
  if (typeof prop.getValue === "function") return prop.getValue(now());
  return prop;
}

function colorFromString(s) {
  const v = (s || "").toString().toLowerCase();
  if (v === "white") return Cesium.Color.WHITE;
  if (v === "yellow") return Cesium.Color.YELLOW;
  if (v === "red") return Cesium.Color.RED;
  return null;
}

function isEntity(obj) {
  if (!obj) return false;
  return (
    typeof obj === "object" &&
    "id" in obj &&
    (obj.position ||
      obj.point ||
      obj.polyline ||
      obj.polygon ||
      obj.billboard ||
      obj.label ||
      obj.rectangle ||
      obj.ellipse ||
      obj.corridor ||
      obj.wall ||
      obj.model ||
      obj.path ||
      obj.box ||
      obj.cylinder ||
      obj.plane)
  );
}

function getEntityFromPick(pick) {
  const id = pick?.id;
  if (isEntity(id)) return id;
  if (id && typeof id === "object" && isEntity(id.id)) return id.id;
  return null;
}

function inLaneMarkerDs(entity) {
  if (!laneMarkerDs?.entities?.contains || !entity) return false;
  try {
    return laneMarkerDs.entities.contains(entity);
  } catch {
    return false;
  }
}

function clampRect(a, b) {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);
  return { left, right, top, bottom, width: right - left, height: bottom - top };
}

function ensureOverlay(viewer) {
  if (overlay) return overlay;

  const c = viewer?.container;
  if (!c) return null;

  overlay = document.createElement("div");
  overlay.style.position = "absolute";
  overlay.style.left = "0px";
  overlay.style.top = "0px";
  overlay.style.width = "0px";
  overlay.style.height = "0px";
  overlay.style.border = "2px solid #ff0000";
  overlay.style.background = "rgba(255,0,0,0.08)";
  overlay.style.pointerEvents = "none";
  overlay.style.display = "none";
  overlay.style.zIndex = "9999";

  c.style.position = c.style.position || "relative";
  c.appendChild(overlay);

  return overlay;
}

function hideOverlay() {
  if (!overlay) return;
  overlay.style.display = "none";
  overlay.style.width = "0px";
  overlay.style.height = "0px";
}

function showOverlay(rect) {
  if (!overlay) return;
  overlay.style.display = "block";
  overlay.style.left = `${rect.left}px`;
  overlay.style.top = `${rect.top}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = `${rect.height}px`;
}

function removeOverlay() {
  if (!overlay) return;
  try {
    overlay.remove();
  } catch {}
  overlay = null;
}

function lockCamera(viewer) {
  if (cameraLocked) return;
  if (!originalCameraControlsRef) return;

  const ok = disableCameraControls(viewer, originalCameraControlsRef);
  if (ok) cameraLocked = true;
}

function unlockCamera(viewer) {
  if (!cameraLocked) return;
  if (!originalCameraControlsRef) {
    cameraLocked = false;
    return;
  }

  restoreCameraControls(viewer, originalCameraControlsRef);
  originalCameraControlsRef.current = null;
  cameraLocked = false;
}

function getEntityKind(entity) {
  if (!entity) return "unknown";
  if (entity.polyline) return "polyline";
  if (entity.corridor) return "corridor";
  if (entity.polygon) return "polygon";
  if (entity.point) return "point";
  if (entity.billboard) return "billboard";
  if (entity.label) return "label";
  return "unknown";
}

function snapshotStyle(entity) {
  const kind = getEntityKind(entity);

  if (kind === "polyline") {
    return {
      kind,
      width: entity.polyline.width,
      material: entity.polyline.material,
      clampToGround: entity.polyline.clampToGround,
      arcType: entity.polyline.arcType,
      disableDepthTestDistance: entity.polyline.disableDepthTestDistance,
    };
  }

  if (kind === "corridor") {
    return {
      kind,
      width: entity.corridor.width,
      material: entity.corridor.material,
      heightReference: entity.corridor.heightReference,
      extrudedHeight: entity.corridor.extrudedHeight,
    };
  }

  if (kind === "polygon") {
    return {
      kind,
      material: entity.polygon.material,
      outline: entity.polygon.outline,
      outlineColor: entity.polygon.outlineColor,
      outlineWidth: entity.polygon.outlineWidth,
      disableDepthTestDistance: entity.polygon.disableDepthTestDistance,
    };
  }

  if (kind === "point") {
    return {
      kind,
      pixelSize: entity.point.pixelSize,
      color: entity.point.color,
      outlineColor: entity.point.outlineColor,
      outlineWidth: entity.point.outlineWidth,
    };
  }

  if (kind === "billboard") {
    return {
      kind,
      color: entity.billboard.color,
      scale: entity.billboard.scale,
      image: entity.billboard.image,
    };
  }

  if (kind === "label") {
    return {
      kind,
      fillColor: entity.label.fillColor,
      outlineColor: entity.label.outlineColor,
      outlineWidth: entity.label.outlineWidth,
      scale: entity.label.scale,
    };
  }

  return { kind };
}

function restoreStyle(entity, snap) {
  if (!entity || !snap) return;

  if (snap.kind === "polyline" && entity.polyline) {
    entity.polyline.width = snap.width;
    entity.polyline.material = snap.material;
    entity.polyline.clampToGround = snap.clampToGround;
    entity.polyline.arcType = snap.arcType;
    entity.polyline.disableDepthTestDistance = snap.disableDepthTestDistance;
    return;
  }

  if (snap.kind === "corridor" && entity.corridor) {
    entity.corridor.width = snap.width;
    entity.corridor.material = snap.material;
    entity.corridor.heightReference = snap.heightReference;
    entity.corridor.extrudedHeight = snap.extrudedHeight;
    return;
  }

  if (snap.kind === "polygon" && entity.polygon) {
    entity.polygon.material = snap.material;
    entity.polygon.outline = snap.outline;
    entity.polygon.outlineColor = snap.outlineColor;
    entity.polygon.outlineWidth = snap.outlineWidth;
    entity.polygon.disableDepthTestDistance = snap.disableDepthTestDistance;
    return;
  }

  if (snap.kind === "point" && entity.point) {
    entity.point.pixelSize = snap.pixelSize;
    entity.point.color = snap.color;
    entity.point.outlineColor = snap.outlineColor;
    entity.point.outlineWidth = snap.outlineWidth;
    return;
  }

  if (snap.kind === "billboard" && entity.billboard) {
    entity.billboard.color = snap.color;
    entity.billboard.scale = snap.scale;
    entity.billboard.image = snap.image;
    return;
  }

  if (snap.kind === "label" && entity.label) {
    entity.label.fillColor = snap.fillColor;
    entity.label.outlineColor = snap.outlineColor;
    entity.label.outlineWidth = snap.outlineWidth;
    entity.label.scale = snap.scale;
  }
}

function applyStyleFromProperties(entity) {
  if (!entity) return;

  const subtype = (getProp(entity, "subtype") || "").toString().toLowerCase();
  const colorStr = (getProp(entity, "color") || "").toString().toLowerCase();

  const c = colorFromString(colorStr) || Cesium.Color.YELLOW;
  const dashed = subtype === "dashed";

  const kind = getEntityKind(entity);

  if (kind === "polyline" && entity.polyline) {
    if (dashed) entity.polyline.material = new Cesium.PolylineDashMaterialProperty({ color: c, dashLength: 16 });
    else entity.polyline.material = new Cesium.ColorMaterialProperty(c);
    return;
  }

  if (kind === "corridor" && entity.corridor) {
    if (dashed) entity.corridor.material = new Cesium.PolylineDashMaterialProperty({ color: c, dashLength: 16 });
    else entity.corridor.material = new Cesium.ColorMaterialProperty(c);
    return;
  }

  if (kind === "polygon" && entity.polygon) {
    entity.polygon.outline = true;
    entity.polygon.outlineColor = c;
    return;
  }

  if (kind === "point" && entity.point) {
    entity.point.color = c;
    entity.point.outlineColor = c;
    return;
  }

  if (kind === "billboard" && entity.billboard) {
    entity.billboard.color = c;
    return;
  }

  if (kind === "label" && entity.label) {
    entity.label.fillColor = c;
    entity.label.outlineColor = c;
  }
}

function highlightOn(entity) {
  if (!entity) return;
  const id = String(entity.id);

  if (!originalStyles.has(id)) originalStyles.set(id, snapshotStyle(entity));

  const kind = getEntityKind(entity);
  const red = Cesium.Color.RED;

  if (kind === "point" && entity.point) {
    const old = entity.point.pixelSize;
    const n = typeof old === "number" ? old : 10;
    entity.point.color = red;
    entity.point.outlineColor = red;
    entity.point.outlineWidth = 2;
    entity.point.pixelSize = n + 3;
    return;
  }

  if (kind === "polyline" && entity.polyline) {
    const oldW = entity.polyline.width;
    const n = typeof oldW === "number" ? oldW : 2;
    entity.polyline.material = new Cesium.ColorMaterialProperty(red);
    entity.polyline.width = n + 2;
    entity.polyline.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    return;
  }

  if (kind === "corridor" && entity.corridor) {
    const oldW = entity.corridor.width;
    const n = typeof oldW === "number" ? oldW : 2;
    entity.corridor.material = new Cesium.ColorMaterialProperty(red);
    entity.corridor.width = n + 1;
    return;
  }

  if (kind === "polygon" && entity.polygon) {
    const ow = entity.polygon.outlineWidth;
    const n = typeof ow === "number" ? ow : 1;
    entity.polygon.outline = true;
    entity.polygon.outlineColor = red;
    entity.polygon.outlineWidth = n + 2;
    entity.polygon.material = Cesium.Color.RED.withAlpha(0.2);
    entity.polygon.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    return;
  }

  if (kind === "billboard" && entity.billboard) {
    const oldScale = entity.billboard.scale;
    const s = typeof oldScale === "number" ? oldScale : 1;
    entity.billboard.color = red;
    entity.billboard.scale = s * 1.15;
    return;
  }

  if (kind === "label" && entity.label) {
    const oldScale = entity.label.scale;
    const s = typeof oldScale === "number" ? oldScale : 1;
    entity.label.fillColor = red;
    entity.label.outlineColor = red;
    entity.label.outlineWidth = 2;
    entity.label.scale = s * 1.1;
  }
}

function highlightOff(entity) {
  if (!entity) return;
  const id = String(entity.id);
  const snap = originalStyles.get(id);
  restoreStyle(entity, snap);
  originalStyles.delete(id);
}

function selectEntity(entity) {
  if (!entity?.id) return;
  const id = String(entity.id);
  if (selectedIds.has(id)) return;
  selectedIds.add(id);
  highlightOn(entity);
}

function unselectEntity(entity) {
  if (!entity?.id) return;
  const id = String(entity.id);
  if (!selectedIds.has(id)) return;
  selectedIds.delete(id);
  highlightOff(entity);
}

function toggleSelect(entity) {
  if (!entity?.id) return;
  const id = String(entity.id);
  if (selectedIds.has(id)) unselectEntity(entity);
  else selectEntity(entity);
}

function clearSelection(applyFinalStyle = false) {
  if (!laneMarkerDs?.entities) {
    selectedIds.clear();
    originalStyles.clear();
    return;
  }

  const ids = Array.from(selectedIds);

  for (const id of ids) {
    const ent = laneMarkerDs.entities.getById(id);
    if (!ent) continue;

    if (applyFinalStyle) {
      const snap = originalStyles.get(id);
      if (snap) restoreStyle(ent, snap);
      applyStyleFromProperties(ent);
      originalStyles.delete(id);
    } else {
      highlightOff(ent);
    }
  }

  selectedIds.clear();
}

// ---- Fast box selection (no pixel scanning) ----

function getValueMaybe(prop, t) {
  if (!prop) return undefined;
  if (typeof prop.getValue === "function") return prop.getValue(t);
  return prop;
}

function collectPolygonPositions(h) {
  const out = [];
  if (!h) return out;
  if (Array.isArray(h.positions)) out.push(...h.positions);
  if (Array.isArray(h.holes)) {
    for (const hole of h.holes) out.push(...collectPolygonPositions(hole));
  }
  return out;
}

function getEntityWorldPositions(entity, t) {
  const kind = getEntityKind(entity);

  if (kind === "polyline" && entity.polyline?.positions) {
    const ps = getValueMaybe(entity.polyline.positions, t);
    return Array.isArray(ps) ? ps : [];
  }

  if (kind === "corridor" && entity.corridor?.positions) {
    const ps = getValueMaybe(entity.corridor.positions, t);
    return Array.isArray(ps) ? ps : [];
  }

  if (kind === "polygon" && entity.polygon?.hierarchy) {
    const h = getValueMaybe(entity.polygon.hierarchy, t);
    return collectPolygonPositions(h);
  }

  if (entity.position) {
    const p = getValueMaybe(entity.position, t);
    return p ? [p] : [];
  }

  return [];
}

const _tmp2 = new Cesium.Cartesian2();

function toCanvas(scene, world) {
  if (!world) return null;
  if (typeof scene.cartesianToCanvasCoordinates === "function") {
    const r = scene.cartesianToCanvasCoordinates(world, _tmp2);
    return r ? { x: r.x, y: r.y } : null;
  }
  const r = Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, world, _tmp2);
  return r ? { x: r.x, y: r.y } : null;
}

function inflateRect(rect, pad) {
  return {
    left: rect.left - pad,
    right: rect.right + pad,
    top: rect.top - pad,
    bottom: rect.bottom + pad,
  };
}

function pointInRect(p, r) {
  return p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
}

// Cohen–Sutherland
function _outCode(x, y, r) {
  let c = 0;
  if (x < r.left) c |= 1;
  else if (x > r.right) c |= 2;
  if (y < r.top) c |= 4;
  else if (y > r.bottom) c |= 8;
  return c;
}

function segmentIntersectsRect(a, b, r) {
  let x0 = a.x,
    y0 = a.y,
    x1 = b.x,
    y1 = b.y;
  let c0 = _outCode(x0, y0, r);
  let c1 = _outCode(x1, y1, r);

  while (true) {
    if ((c0 | c1) === 0) return true;
    if ((c0 & c1) !== 0) return false;

    const cOut = c0 !== 0 ? c0 : c1;
    let x = 0,
      y = 0;

    if (cOut & 8) {
      x = x0 + ((x1 - x0) * (r.bottom - y0)) / (y1 - y0);
      y = r.bottom;
    } else if (cOut & 4) {
      x = x0 + ((x1 - x0) * (r.top - y0)) / (y1 - y0);
      y = r.top;
    } else if (cOut & 2) {
      y = y0 + ((y1 - y0) * (r.right - x0)) / (x1 - x0);
      x = r.right;
    } else {
      y = y0 + ((y1 - y0) * (r.left - x0)) / (x1 - x0);
      x = r.left;
    }

    if (cOut === c0) {
      x0 = x;
      y0 = y;
      c0 = _outCode(x0, y0, r);
    } else {
      x1 = x;
      y1 = y;
      c1 = _outCode(x1, y1, r);
    }
  }
}

function pointInPolygon(pt, vs) {
  let inside = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const xi = vs[i].x,
      yi = vs[i].y;
    const xj = vs[j].x,
      yj = vs[j].y;
    const intersect = yi > pt.y !== yj > pt.y && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function getPolylinePadPx(entity) {
  const t = now();
  const wProp = entity?.polyline?.width;
  let w = 2;
  if (typeof wProp === "number") w = wProp;
  else if (wProp && typeof wProp.getValue === "function") {
    const v = wProp.getValue(t);
    if (typeof v === "number") w = v;
  }
  return Math.max(4, Math.ceil(w / 2) + 3);
}

function entityIntersectsRect(viewer, entity, rect) {
  const scene = viewer.scene;
  const t = now();
  const kind = getEntityKind(entity);

  let pad = 4;
  if (kind === "polyline") pad = getPolylinePadPx(entity);
  else if (kind === "corridor") pad = 8;
  else if (kind === "polygon") pad = 6;

  const r = inflateRect(rect, pad);

  const worldPositions = getEntityWorldPositions(entity, t);
  if (!worldPositions || worldPositions.length === 0) return false;

  // Point-like
  if (kind === "point" || kind === "billboard" || kind === "label") {
    const p = toCanvas(scene, worldPositions[0]);
    return p ? pointInRect(p, r) : false;
  }

  // Project vertices
  const pts = [];
  for (let i = 0; i < worldPositions.length; i++) {
    const p = toCanvas(scene, worldPositions[i]);
    if (p) pts.push(p);
  }
  if (pts.length === 0) return false;

  // Any vertex inside
  for (let i = 0; i < pts.length; i++) {
    if (pointInRect(pts[i], r)) return true;
  }

  // Any segment intersects
  for (let i = 1; i < pts.length; i++) {
    if (segmentIntersectsRect(pts[i - 1], pts[i], r)) return true;
  }

  // Polygon covers rect center
  if (kind === "polygon" && pts.length >= 3) {
    const center = { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
    if (pointInPolygon(center, pts)) return true;
  }

  return false;
}

function nextFrame() {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

async function pickEntitiesInRectFast(viewer, rect, token) {
  const out = [];
  const vals = laneMarkerDs?.entities?.values || [];
  const chunk = 250;

  for (let i = 0; i < vals.length; i++) {
    if (!laneMarkerStyleModeEnabled) return [];
    if (token !== pickOpToken) return [];

    const ent = vals[i];
    if (!ent) continue;
    if (ent.show === false) continue;
    if (!inLaneMarkerDs(ent)) continue;

    if (entityIntersectsRect(viewer, ent, rect)) out.push(ent);

    if (i > 0 && i % chunk === 0) await nextFrame();
  }

  return out;
}

// ---- Lifecycle ----

function destroyInternal(viewer) {
  laneMarkerStyleModeEnabled = false;

  pickOpToken++;

  clearSelection(false);

  if (handler) {
    try {
      handler.destroy();
    } catch {}
    handler = null;
  }

  if (keyDownHandler) {
    window.removeEventListener("keydown", keyDownHandler);
    keyDownHandler = null;
  }

  dragging = false;
  dragStart = null;
  dragEnd = null;
  dragMoved = false;

  hideOverlay();
  unlockCamera(viewer);

  removeOverlay();

  laneMarkerDs = null;
  onRequestDisableRef = null;

  originalCameraControlsRef = null;
  cameraLocked = false;
}

function onKeyDown(e, viewer, onEdited, userData) {
  if (!laneMarkerStyleModeEnabled) return;

  const k = (e.key || "").toLowerCase();

  if (k === "escape") {
    if (selectedIds.size > 0) {
      clearSelection(false);
      e.preventDefault?.();
      e.stopPropagation?.();
      return;
    }

    e.preventDefault?.();
    e.stopPropagation?.();

    try {
      if (typeof onRequestDisableRef === "function") onRequestDisableRef();
    } catch {}

    destroyInternal(viewer);
    return;
  }

  if (!selectedIds.size) return;

  let didChange = false;

  if (k === "w") {
    didChange = true;
    for (const id of selectedIds) {
      const ent = laneMarkerDs.entities.getById(id);
      if (ent) setProp(ent, "color", "white");
    }
  } else if (k === "y") {
    didChange = true;
    for (const id of selectedIds) {
      const ent = laneMarkerDs.entities.getById(id);
      if (ent) setProp(ent, "color", "yellow");
    }
  } else if (k === "d") {
    didChange = true;
    for (const id of selectedIds) {
      const ent = laneMarkerDs.entities.getById(id);
      if (ent) setProp(ent, "subtype", "dashed");
    }
  } else if (k === "s") {
    didChange = true;
    for (const id of selectedIds) {
      const ent = laneMarkerDs.entities.getById(id);
      if (ent) setProp(ent, "subtype", "solid");
    }
  } else {
    return;
  }

  if (!didChange) return;

  const attEditor = getUsernameAndUserIdFormat(userData);

  if (attEditor) {
    for (const id of selectedIds) {
      const ent = laneMarkerDs.entities.getById(id);
      if (ent) setProp(ent, "att_editor", attEditor);
    }
  }

  clearSelection(true);

  const msg = k === "w" ? "Applied: color = white" : k === "y" ? "Applied: color = yellow" : k === "d" ? "Applied: subtype = dashed" : k === "s" ? "Applied: subtype = solid" : null;

  if (msg) {
    showToast(msg, Type.NOTIFICATION.SUCCESS, {
      duration: 1200,
      closable: true,
    });
  }

  try {
    if (typeof onEdited === "function") onEdited();
  } catch {}
}

/**
 * toggleLaneMarkerStyleTool(viewer, onEdited, destroy, laneMarkerDataSource, onRequestDisable)
 */
export function toggleLaneMarkerStyleTool(viewer, onEdited, destroy = false, laneMarkerDataSource = null, onRequestDisable = null, originalCameraControls, userData) {
  if (!viewer) return false;
  originalCameraControlsRef = originalCameraControls || originalCameraControlsRef;

  if (destroy === true) {
    destroyInternal(viewer);
    return false;
  }

  if (!laneMarkerDataSource?.entities) return false;

  if (laneMarkerStyleModeEnabled && handler) {
    laneMarkerDs = laneMarkerDataSource;
    onRequestDisableRef = typeof onRequestDisable === "function" ? onRequestDisable : null;

    if (keyDownHandler) {
      try {
        window.removeEventListener("keydown", keyDownHandler);
      } catch {}
      keyDownHandler = null;
    }

    keyDownHandler = (e) => onKeyDown(e, viewer, onEdited, userData);
    window.addEventListener("keydown", keyDownHandler);
    return true;
  }

  laneMarkerStyleModeEnabled = true;
  laneMarkerDs = laneMarkerDataSource;
  onRequestDisableRef = typeof onRequestDisable === "function" ? onRequestDisable : null;

  ensureOverlay(viewer);

  if (handler) {
    try {
      handler.destroy();
    } catch {}
    handler = null;
  }

  handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  handler.setInputAction((movement) => {
    if (!laneMarkerStyleModeEnabled) return;

    lockCamera(viewer);

    dragging = true;
    dragMoved = false;
    dragStart = movement.position;
    dragEnd = movement.position;
    hideOverlay();
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement) => {
    if (!laneMarkerStyleModeEnabled) return;
    if (!dragging || !dragStart) return;

    dragEnd = movement.endPosition;

    const dx = Math.abs(dragEnd.x - dragStart.x);
    const dy = Math.abs(dragEnd.y - dragStart.y);
    if (dx > 5 || dy > 5) dragMoved = true;

    if (dragMoved) showOverlay(clampRect(dragStart, dragEnd));
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(async (movement) => {
    if (!laneMarkerStyleModeEnabled) return;

    const endPos = movement.position;
    const startPos = dragStart;

    dragging = false;

    let rect = null;

    try {
      if (!startPos || !endPos) return;
      rect = clampRect(startPos, endPos);
    } finally {
      dragStart = null;
      dragEnd = null;
      dragMoved = false;
      hideOverlay();
      unlockCamera(viewer);
    }

    if (!rect) return;

    if (rect.width > 6 && rect.height > 6) {
      const token = ++pickOpToken;
      const found = await pickEntitiesInRectFast(viewer, rect, token);
      if (!laneMarkerStyleModeEnabled) return;
      if (token !== pickOpToken) return;
      for (const ent of found) selectEntity(ent);
    } else {
      const picked = viewer.scene.pick(endPos);
      const ent = getEntityFromPick(picked);
      if (ent && inLaneMarkerDs(ent)) toggleSelect(ent);
    }
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  if (keyDownHandler) {
    try {
      window.removeEventListener("keydown", keyDownHandler);
    } catch {}
    keyDownHandler = null;
  }

  keyDownHandler = (e) => onKeyDown(e, viewer, onEdited, userData);
  window.addEventListener("keydown", keyDownHandler);

  return true;
}
